import { useQuery } from '@tanstack/react-query';
import ordersApi from '../api/orders';
import { normalizePromoCode, orderPriceRequest } from '../lib/orderPriceRequest';

// The backend currently rejects an entirely uncovered cart instead of returning a promo verdict.
const UNCOVERED_PRODUCTS = 'This coupon is not valid for the selected products.';
const UNKNOWN_COUPON = 'No coupon is matching given promo code.';

export const orderPriceOptions = ({
  accessToken, items, countryCode, shippingType = -1, promoCode, paymentMethod,
}) => ({
  queryKey: [
    'order-price',
    accessToken ?? null,
    orderPriceRequest(items, countryCode, shippingType, promoCode, paymentMethod),
  ],
  queryFn: async () => {
    const code = normalizePromoCode(promoCode);
    const { data, error, status, reason } = await ordersApi(countryCode).getOrderPrice(
      accessToken,
      items,
      shippingType,
      code,
      paymentMethod,
    );
    if (code && status === 400 && [UNCOVERED_PRODUCTS, UNKNOWN_COUPON].includes(error)) {
      const fallback = await ordersApi(countryCode).getOrderPrice(
        accessToken, items, shippingType, '', paymentMethod,
      );
      if (fallback.error) throw new Error(fallback.error);
      return {
        ...fallback.data,
        orderPromoCode: '',
        ...(error === UNKNOWN_COUPON
          // The message itself means not_found, also before the backend sends `reason`.
          ? { rejectedPromoCode: code, rejectedPromoReason: reason ?? 'not_found' }
          : { excludedPromoCode: code }),
      };
    }
    // `reason` names a refused promo code (Coupon::REJECTION_REASON on the backend).
    if (error) throw Object.assign(new Error(error), { reason });
    return { ...data, orderPromoCode: code };
  },
  // Live account credit and coupon limits require fresh quotes, with only active queries retained.
  staleTime: 0,
  gcTime: 0,
  retry: false,
});

// A cart edit may retain a catalog minimum confirmation while its replacement is pending.
// A different account, country or claim must never inherit that confirmation.
export const previousOrderPrice = (queryKey) => (data, previousQuery) => {
  const [, account, request] = queryKey;
  const [, previousAccount, previousRequest] = previousQuery?.queryKey ?? [];
  return account === previousAccount &&
    request.country_alpha2 === previousRequest?.country_alpha2 &&
    request.promo_code === previousRequest?.promo_code
    ? data
    : undefined;
};

const useOrderPrice = ({ enabled = true, keepPrevious = false, ...input }) => {
  const options = orderPriceOptions(input);
  const canPrice = enabled && input.items.length > 0 && Boolean(input.countryCode);
  const query = useQuery({
    ...options,
    enabled: canPrice,
    placeholderData: keepPrevious ? previousOrderPrice(options.queryKey) : undefined,
  });
  return { ...query, data: canPrice && !query.isError ? query.data : undefined };
};

export default useOrderPrice;
